import { isNative } from "./platform";
import { load, save } from "./storage";

const API_KEY = "omni.apiBase";
const TOKENS_KEY = "omni.tokens";

export interface Tokens {
  access_token: string;
  refresh_token: string;
}

/** Where the Omni server lives. Mobile builds can change it in Settings. */
export function apiBase(): string {
  const override = load<string | null>(API_KEY, null);
  if (override) return override.replace(/\/$/, "");
  const env = import.meta.env.VITE_API_BASE as string | undefined;
  // Set at build time. Empty string = same origin (the Docker/Nginx deployment).
  if (env !== undefined) return env ? env.replace(/\/$/, "") : location.origin;
  return isNative ? "https://omnimind.club" : `${location.protocol}//${location.hostname}:8000`;
}

/** Public web address of the shell, used for shareable links (profiles, invites). */
export function shellUrl(): string {
  const env = import.meta.env.VITE_SHELL_URL as string | undefined;
  if (env) return env.replace(/\/$/, "");
  return isNative ? "https://omnimind.club" : location.origin;
}

/** Resolve a URL from the API (icons, app bundles) against the API base. */
export function resolveUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  return /^(https?:|data:|blob:)/.test(url) ? url : `${apiBase()}${url.startsWith("/") ? "" : "/"}${url}`;
}

export function setApiBase(url: string | null): void {
  save(API_KEY, url ? url.replace(/\/$/, "") : null);
}

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

let tokens: Tokens | null = load<Tokens | null>(TOKENS_KEY, null);
let refreshing: Promise<boolean> | null = null;
const listeners = new Set<() => void>();

export function getTokens(): Tokens | null {
  return tokens;
}

export function setTokens(next: Tokens | null): void {
  tokens = next ? { access_token: next.access_token, refresh_token: next.refresh_token } : null;
  save(TOKENS_KEY, tokens);
  listeners.forEach((fn) => fn());
}

export function onTokensChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export async function errorFrom(res: Response): Promise<ApiError> {
  let message = res.statusText || `HTTP ${res.status}`;
  try {
    const body = await res.json();
    if (typeof body.detail === "string") message = body.detail;
    else if (Array.isArray(body.detail)) message = body.detail.map((d: { msg: string }) => d.msg).join(", ");
  } catch {
    /* not JSON */
  }
  return new ApiError(message, res.status);
}

/** Get a fresh access token with the refresh token (false when signed out). */
export function refreshTokens(): Promise<boolean> {
  return refresh();
}

async function refresh(): Promise<boolean> {
  if (!tokens) return false;
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${apiBase()}/api/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: tokens!.refresh_token }),
      });
      if (!res.ok) {
        setTokens(null);
        return false;
      }
      setTokens(await res.json());
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

type Query = Record<string, string | number | boolean | undefined | null>;

export function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  const s = qs.toString();
  return s ? `${path}${path.includes("?") ? "&" : "?"}${s}` : path;
}

/** Account-level API call with the user's token (auto-refresh on 401). */
export async function api<T = unknown>(
  path: string,
  init: { method?: string; body?: unknown; query?: Query; auth?: boolean } = {},
): Promise<T> {
  const res = await send(path, init);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Like `api`, but returns the response body as a Blob (e.g. an image). */
export async function apiBlob(path: string): Promise<Blob> {
  return (await send(path, {})).blob();
}

async function send(path: string, init: { method?: string; body?: unknown; query?: Query; auth?: boolean }): Promise<Response> {
  // FormData (file uploads) goes as multipart; anything else as JSON.
  const form = init.body instanceof FormData;
  const doFetch = () => {
    const headers: Record<string, string> = {};
    if (init.body !== undefined && !form) headers["Content-Type"] = "application/json";
    if (init.auth !== false && tokens) headers.Authorization = `Bearer ${tokens.access_token}`;
    return fetch(`${apiBase()}${withQuery(path, init.query)}`, {
      method: init.method ?? (init.body !== undefined ? "POST" : "GET"),
      headers,
      body: init.body === undefined ? undefined : form ? (init.body as FormData) : JSON.stringify(init.body),
    });
  };
  let res = await doFetch();
  if (res.status === 401 && init.auth !== false && (await refresh())) res = await doFetch();
  if (!res.ok) throw await errorFrom(res);
  return res;
}

/** ADR-0008 page shape. */
export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
  sort: string;
}
